import { FC, useEffect, useState } from 'react';
import { DrugItem } from 'src/api/types';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { GTAGPrefix } from 'src/hooks/useGTAG';
import { FormattedMessage } from 'react-intl';
import Skeleton from 'src/shared-components/Loaders/Skeleton';
import { DrugCardVariant } from '../DrugCard/types';
import SkeletonLoaders from '../Loaders/SkeletonLoaders';
import trns from '../Button/trns';

const LoadingButton = dynamic(() => import('../design-system/LoadingButton'), {
  ssr: false
});

const DrugCard = dynamic(() => import('../DrugCard/DrugCard'), {
  loading: () => <Skeleton width={'100%'} height={120} />
});

interface Props {
  isLoading: boolean;
  drugsList?: DrugItem[];
  gtagPrefix: GTAGPrefix;
  addToCartGAAction?: string;
  clickOnCardGAAction?: string;
}

const MobileDrugList: FC<Props> = ({
  isLoading,
  drugsList,
  gtagPrefix,
  addToCartGAAction,
  clickOnCardGAAction
}) => {
  const [croppedList, setCroppedList] = useState<DrugItem[]>([]);
  const [numberOfCalls, setNumberOfCalls] = useState<number>(1);

  useEffect(
    () => {
      if (drugsList?.length) {
        setCroppedList(drugsList.slice(0, 2));
      }
    },
    // Map drugIds as string dependency instead of the drugs array to avoid unwanted useEffect triggers
    [drugsList?.map((drug) => drug.id.toString()).join()]
  );

  const handleLoadingClick = () => {
    setCroppedList(drugsList.slice(0, numberOfCalls * 6 + 2));
    setNumberOfCalls(numberOfCalls + 1);
  };

  return (
    <div className="flex w-full items-center justify-center">
      <div
        className={cn(
          'flex h-[300px] w-full flex-col pt-2',
          !isLoading && 'hidden'
        )}
      >
        <div className="w-full grid grid-cols-2 gap-4 px-7.5">
          <SkeletonLoaders
            loaderList={[
              { height: 299, width: '100%' },
              { height: 299, width: '100%' }
            ]}
          />
        </div>
      </div>
      <div className={cn('flex w-full flex-col pt-2', isLoading && 'hidden')}>
        <div className="w-full grid grid-cols-2 s:gap-4">
          {croppedList.map((drug) => {
            return (
              <DrugCard
                key={drug.id}
                drug={drug}
                variant={DrugCardVariant.MinimalisticCard}
                gtagPrefix={gtagPrefix}
                addToCartGAAction={addToCartGAAction}
                clickOnCardGAAction={clickOnCardGAAction}
              />
            );
          })}
        </div>
        {croppedList?.length < drugsList?.length && (
          <div className="w-full mt-6 mb-6">
            {' '}
            <LoadingButton
              onClick={handleLoadingClick}
              buttonLabel={<FormattedMessage {...trns.loadMore} />}
            />{' '}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileDrugList;
